import { useTranslation } from "react-i18next";
import { Message } from "../../features/messages/models/Message";
import { MessagesChannel } from "../../features/messages/models/MessagesChannel";
import DeleteMessageButton from "./DeleteMessageButton";
import MessageAttachments from "./MessageAttachments";
import ReportButton from "./ReportButton";

interface MessageRowProps {
  message: Message;
  /** The Direct thread's subject member (another member writing is their guardian); null elsewhere. */
  subjectId: string | null;
  /** The face the attachments' URLs are fetched through. */
  channel: MessagesChannel;
}

/** An erased member's message keeps its row with no author: their name and words are gone (PD-0032). */
const isErased = (message: Message): boolean =>
  message.authorKind === "Member" && message.authorId === null && message.deletedAt === null;

/** Someone else's live message the reader has not reported yet may be reported (US-111). */
const isReportable = (message: Message): boolean =>
  !message.isMine && message.kind !== "System" && message.deletedAt === null && !message.reportedByMe &&
  !isErased(message);

/**
 * One message: the author and time in a muted line, then the body with its line breaks and its
 * attachments. A deleted or erased message stays as a placeholder so the thread keeps its shape.
 */
const MessageRow = ({ message, subjectId, channel }: MessageRowProps) => {
  const { t, i18n } = useTranslation();
  const time = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
    message.createdAt,
  );
  const erased = isErased(message);
  const placeholder = message.deletedAt
    ? t("messages.deleted")
    : erased
      ? t("messages.erased")
      : message.reportedByMe
        ? t("messages.reported")
        : null;

  const author = (() => {
    if (erased) return "";
    if (message.isMine) return t("messages.thread.you");
    const name = message.authorName ?? "";
    const isGuardian = subjectId !== null && message.authorKind === "Member" && message.authorId !== subjectId;
    return isGuardian && name ? t("messages.thread.guardian", { name }) : name;
  })();

  return (
    <li className="py-3" data-testid="message-row" data-message-id={message.id}>
      <div className="flex items-center gap-2">
        <p className="text-xs text-grey-40 dark:text-grey-80">
          {author && (
            <>
              <span className="font-semibold text-grey-20 dark:text-white">{author}</span>
              <span aria-hidden="true"> · </span>
            </>
          )}
          <time dateTime={message.createdAt.toISOString()}>{time}</time>
        </p>
        {message.canDelete && <DeleteMessageButton channel={channel} message={message} />}
        {isReportable(message) && (
          <ReportButton
            face={channel.kind === "staff" ? "staff" : "member"}
            target={{ targetKind: "Message", targetId: message.id, threadId: message.threadId }}
            alignEnd={!message.canDelete}
          />
        )}
      </div>
      {placeholder ? (
        <p className="mt-1 text-sm italic text-grey-40 dark:text-grey-80" data-testid="message-placeholder">
          {placeholder}
        </p>
      ) : (
        <>
          {message.body && (
            <p className="mt-1 whitespace-pre-wrap break-words text-grey-20 dark:text-white">{message.body}</p>
          )}
          <MessageAttachments attachments={message.attachments} channel={channel} />
        </>
      )}
    </li>
  );
};

export default MessageRow;
